import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAutoLogout } from '@/hooks/useAutoLogout';
import { Button } from '@/components/ui';

/**
 * Idle warning with the sign-out countdown.
 *
 * A slim banner (not a dialog) under the header, centred at the top edge: it never covers the
 * middle of the screen. It is rendered by BaseLayout outside the page's stacking context and
 * sits on the toast layer, so an open dialog cannot hide it.
 */
export default function AutoLogoutWarning() {
  const { t } = useTranslation();
  const { secondsRemaining, countdownSeconds, isWarningVisible, resetTimer } = useAutoLogout();

  if (!isWarningVisible || secondsRemaining <= 0 || typeof document === 'undefined') {
    return null;
  }

  const progress = countdownSeconds > 0 ? Math.min(1, secondsRemaining / countdownSeconds) : 0;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-toast flex justify-center px-3"
      style={{ top: 'calc(var(--wf-header-h, 3.5rem) + env(safe-area-inset-top, 0px) + 0.5rem)' }}
    >
      <div
        role="alert"
        aria-live="assertive"
        data-testid="auto-logout-warning"
        className="auto-logout-warning pointer-events-auto relative flex w-full max-w-140 items-center gap-3 overflow-hidden rounded-container border border-l-4 border-edge border-l-warning bg-raised px-3 py-2 font-ui shadow-raised"
      >
        <AlertTriangle className="size-5 shrink-0 text-warning" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-body font-semibold text-primary">
            {t('autoLogout.title', 'Are you still there?')}
          </p>
          <p className="m-0 text-small text-secondary">
            {t('autoLogout.body', 'For your security you will be signed out in')}{' '}
            <span
              className="font-money font-semibold tabular-nums text-warning"
              data-testid="auto-logout-seconds"
            >
              {t('autoLogout.seconds', '{{count}}s', { count: secondsRemaining })}
            </span>
          </p>
        </div>
        <Button variant="secondary" size="sm" className="shrink-0" onClick={() => resetTimer()}>
          {t('autoLogout.stay', 'Stay signed in')}
        </Button>
        {/* Drains with the countdown: one continuous animation for its whole length, not a step
            per second. --auto-logout-progress is the fallback when animations are switched off. */}
        <span
          aria-hidden
          className="auto-logout-bar absolute inset-x-0 bottom-0 h-0.75 bg-warning"
          style={
            {
              '--auto-logout-duration': `${countdownSeconds}s`,
              '--auto-logout-progress': progress,
            } as React.CSSProperties
          }
        />
      </div>
    </div>
  );
}
